import { useMemo } from 'react';
import { useParams } from 'react-router';

import { useFeatureFlag } from '@/context/FeatureFlagProvider';
import { useLinkToExplorer } from '@/hooks/links';
import { useRawParams, useValidatedParams } from '@/hooks/params';
import { ProjectScope } from '@/hooks/params';
import { UrlEnvironmentType } from '@/typings/url';

interface EnvironmentSwitcherResult {
  productionUrl: string;
  stagingUrl: string;
  devUrl: string;
  environmentUrls: Record<UrlEnvironmentType, string>;
  selectedEnv: string;
  allowDevEnvToggle: boolean;
  environmentScopes: {
    production: ProjectScope;
    staging: ProjectScope;
    development: ProjectScope;
  };
  isProductionEnabled: boolean;
  isStagingEnabled: boolean;
}

export const useEnvironmentSwitcher = (
  productionEnv?: number,
  stagingEnv?: number,
  devEnv?: number,
): EnvironmentSwitcherResult => {
  const hasDevEnvFeature = useFeatureFlag(
    'meta3092ExplorerEnableDevEnvironment',
    false,
  );
  const allowDevEnvToggle = Boolean(hasDevEnvFeature && devEnv);

  const { projectId, environmentType } = useValidatedParams();
  const { accountId } = useRawParams(); // Use raw params for accountId to work in both single pane and normal view

  const environmentScopes = {
    production: {
      accountId: accountId || 0,
      projectId: projectId || 0,
      environmentType: 'production' as const,
    },
    staging: {
      accountId: accountId || 0,
      projectId: projectId || 0,
      environmentType: 'staging' as const,
    },
    development: {
      accountId: accountId || 0,
      projectId: projectId || 0,
      environmentType: 'development' as const,
    },
  };

  const remainder = useParams()['*'] + window.location.search;
  const getEnvironmentUrl = useLinkToExplorer('project');

  const productionUrl = getEnvironmentUrl(environmentScopes.production) + remainder;
  const stagingUrl = getEnvironmentUrl(environmentScopes.staging) + remainder;
  const devUrl = getEnvironmentUrl(environmentScopes.development) + remainder;

  const getSelectedEnv = () => {
    if (environmentType === 'production') {
      return 'prodEnv';
    } else if (environmentType === 'staging') {
      return 'stagingEnv';
    } else if (environmentType === 'development') {
      return 'devEnv';
    }
    return '';
  };

  const environmentUrls = useMemo<Record<UrlEnvironmentType, string>>(
    () => ({
      production: productionUrl,
      staging: stagingUrl,
      development: devUrl,
    }),
    [productionUrl, stagingUrl, devUrl],
  );

  return {
    productionUrl,
    stagingUrl,
    devUrl,
    environmentUrls,
    selectedEnv: getSelectedEnv(),
    allowDevEnvToggle,
    environmentScopes,
    isProductionEnabled: Boolean(productionEnv),
    isStagingEnabled: Boolean(stagingEnv),
  };
};
